import { motion, useReducedMotion } from "framer-motion";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CTAButton } from "@/components/CTAButton";
import EditorialMarquee from "@/components/EditorialMarquee";
import { BUSINESS } from "@/data/site";

const CHAPTERS = [
  {
    n: "01",
    title: "Cooked to order, always",
    body: "Nothing sits under a lamp. Every dish is fired the moment you order it, so it reaches your table at its very best — crisp, hot and full of wok breath.",
    image: "https://images.pexels.com/photos/12455439/pexels-photo-12455439.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1100",
  },
  {
    n: "02",
    title: "Dough stretched by hand",
    body: "Our noodles are pulled fresh in-house. It takes practice and patience, but that springy, chewy bite is worth every minute — you simply can't fake it.",
    image: "https://images.pexels.com/photos/36771456/pexels-photo-36771456.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1100",
  },
  {
    n: "03",
    title: "A corner made warm",
    body: "We designed the room to feel welcoming and contemporary — a place equally right for a solo lunch, a family dinner or a long catch-up with friends.",
    image: "https://images.pexels.com/photos/10135116/pexels-photo-10135116.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1100",
  },
];

export default function About() {
  const reduce = useReducedMotion();
  return (
    <>
      <SEO title="About" description="The story behind Chopstick Chinese Corner in Sector xxx, Gurugram — cooked-to-order Chinese food, hand-pulled noodles and a warm, contemporary space." />
      <PageHero
        overline="Our story"
        titleLines={["A corner", "worth", "returning to."]}
        subtitle="Chopstick Chinese Corner began with a simple belief: great Chinese food is about heat, freshness and hospitality — not fuss."
      />

      <section className="mx-auto max-w-7xl px-5 md:px-8 py-20 md:py-28">
        {CHAPTERS.map((c, i) => (
          <div key={c.n} className="grid gap-8 lg:grid-cols-12 items-start py-12 md:py-16 border-b border-brand-ink/10 last:border-0">
            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-28">
                <span className="font-display text-7xl md:text-8xl text-brand-red/20">{c.n}</span>
                <h2 className="mt-2 font-display text-3xl md:text-4xl text-brand-ink leading-tight">{c.title}</h2>
                <p className="mt-4 text-muted-foreground leading-relaxed max-w-sm">{c.body}</p>
              </div>
            </div>
            <div className="lg:col-span-8 overflow-hidden">
              <motion.img
                src={c.image}
                alt={c.title}
                loading="lazy"
                initial={{ opacity: 0, scale: reduce ? 1 : 1.06 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="w-full h-[340px] md:h-[520px] object-cover"
              />
            </div>
          </div>
        ))}
      </section>

      <EditorialMarquee text="Fresh · Fiery · Made to order ·" />

      <section className="bg-brand-ink text-white py-24 md:py-32">
        <div className="mx-auto max-w-3xl px-5 md:px-8 text-center">
          <Reveal>
            <h2 className="font-display text-4xl md:text-6xl leading-tight">Come say hello.</h2>
            <p className="mt-6 text-white/70 text-lg">
              Find us in {BUSINESS.addressLine2}. We&apos;d love to cook for you.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <CTAButton href={`tel:${BUSINESS.phoneHref}`} variant="gold" icon={false}>Call us</CTAButton>
              <CTAButton to="/menu" variant="outlineLight">View the menu</CTAButton>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
